import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Play, Pause, Square, RotateCw, RefreshCw, ClipboardList,
  Users, TrendingUp, IndianRupee, ExternalLink,
} from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { CAMP } from "@/constants/testIds";
import { useAuth, canCampaignsWrite } from "@/lib/auth";

const STATUS_CLS = {
  DRAFT: "bg-slate-100 text-slate-700",
  RUNNING: "bg-emerald-100 text-emerald-800",
  PAUSED: "bg-amber-100 text-amber-800",
  STOPPED: "bg-rose-100 text-rose-800",
  COMPLETED: "bg-sky-100 text-sky-800",
};

const LEAD_STATUS_CLS = {
  QUEUED: "bg-slate-100 text-slate-700",
  IN_PROGRESS: "bg-sky-100 text-sky-800",
  INTERESTED: "bg-emerald-100 text-emerald-800",
  CALLBACK: "bg-amber-100 text-amber-800",
  NOT_INTERESTED: "bg-rose-100 text-rose-800",
  NO_ANSWER: "bg-slate-100 text-slate-500",
  BUSY: "bg-amber-100 text-amber-700",
  VOICEMAIL: "bg-slate-100 text-slate-500",
  CALL_FAILED: "bg-rose-100 text-rose-700",
  PAYMENT_PENDING: "bg-indigo-100 text-indigo-800",
  PAID: "bg-emerald-100 text-emerald-800",
  RENEWED: "bg-emerald-100 text-emerald-800",
  ATTEMPTS_EXHAUSTED: "bg-rose-100 text-rose-800",
  HUMAN_ASSIGNED: "bg-rose-100 text-rose-800",
  HUMAN_TRANSFER: "bg-rose-100 text-rose-800",
  DND: "bg-slate-200 text-slate-700",
  WRONG_NUMBER: "bg-slate-200 text-slate-700",
  EXPIRED_UNCONVERTED: "bg-slate-200 text-slate-500",
};

function Chip({ text, cls }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls || "bg-slate-100 text-slate-700"}`}>
      {text}
    </span>
  );
}

function StatCard({ label, value, testId, tone }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div data-testid={testId}
        className={`text-2xl font-semibold mt-1 ${tone || "text-slate-900"}`}>
        {value}
      </div>
    </div>
  );
}

export default function CampaignDetailPage() {
  const { campaign_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const writable = canCampaignsWrite(user);

  const [campaign, setCampaign] = useState(null);
  const [report, setReport] = useState(null);
  const [preview, setPreview] = useState(null);
  const [leads, setLeads] = useState([]);
  const [leadsMeta, setLeadsMeta] = useState({ total: 0, total_pages: 1 });
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsMobile, setLeadsMobile] = useState("");
  const [leadsStatus, setLeadsStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [editingFilters, setEditingFilters] = useState(false);
  const [filtersDraft, setFiltersDraft] = useState({});

  const leadsParams = useMemo(() => ({
    page: leadsPage, page_size: 25,
    mobile: leadsMobile || undefined,
    status: leadsStatus || undefined,
  }), [leadsPage, leadsMobile, leadsStatus]);

  const loadCampaign = useCallback(async () => {
    const { data } = await api.get(`/campaigns/${campaign_id}`);
    setCampaign(data);
    setFiltersDraft(data.filters || {});
  }, [campaign_id]);

  const loadReport = useCallback(async () => {
    const { data } = await api.get(`/campaigns/${campaign_id}/report`);
    setReport(data);
  }, [campaign_id]);

  const loadLeads = useCallback(async () => {
    const { data } = await api.get(`/campaigns/${campaign_id}/leads`, { params: leadsParams });
    setLeads(data.items);
    setLeadsMeta(data.meta);
  }, [campaign_id, leadsParams]);

  const loadAll = useCallback(async () => {
    setError(null);
    try {
      await Promise.all([loadCampaign(), loadReport(), loadLeads()]);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load campaign"));
    }
  }, [loadCampaign, loadReport, loadLeads]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const runAction = async (label, method, path, body) => {
    setBusy(true);
    setNotice(null);
    setError(null);
    try {
      const { data } = await api.request({ method, url: path, data: body });
      setNotice(`${label} — OK${data?.enrolled != null ? ` (enrolled ${data.enrolled}, skipped ${data.skipped})` : ""}`);
      await loadAll();
    } catch (e) {
      setError(errorToMessage(e, `${label} failed`));
    } finally {
      setBusy(false);
    }
  };

  const runPreview = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await api.get(`/campaigns/${campaign_id}/preview`);
      setPreview(data);
      setNotice(`Preview: ${data.total} eligible passport(s).`);
    } catch (e) {
      setError(errorToMessage(e, "Preview failed"));
    } finally {
      setBusy(false);
    }
  };

  const saveFilters = async () => {
    await runAction("Filters saved", "PATCH", `/campaigns/${campaign_id}`, {
      filters: filtersDraft,
    });
    setEditingFilters(false);
  };

  const generatePaymentLink = async (leadId) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { data } = await api.post(`/warranty-leads/${leadId}/payment-link`);
      setNotice(`Payment link generated: ${data.link_url}`);
      await loadLeads();
      await loadReport();
    } catch (e) {
      setError(errorToMessage(e, "Payment link generation failed"));
    } finally {
      setBusy(false);
    }
  };

  if (!campaign) {
    return (
      <div className="p-10 text-center text-slate-400" data-testid={CAMP.detailPage}>
        {error || "Loading campaign…"}
      </div>
    );
  }

  const st = campaign.status;

  return (
    <div className="space-y-6" data-testid={CAMP.detailPage}>
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/admin/campaigns")}
            className="rounded-md border border-slate-300 bg-white p-1.5 text-slate-700 hover:bg-slate-50">
            <ArrowLeft size={14} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900"
                data-testid={CAMP.detailName}>{campaign.name}</h1>
              <span data-testid={CAMP.detailStatus}>
                <Chip text={st} cls={STATUS_CLS[st]} />
              </span>
            </div>
            {campaign.description && (
              <p className="text-sm text-slate-500 mt-1 max-w-xl">{campaign.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadAll}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm">
            <RefreshCw size={14} /> Refresh
          </button>
          {writable && st === "DRAFT" && (
            <button onClick={() => runAction("Started", "POST", `/campaigns/${campaign_id}/start`)}
              disabled={busy} data-testid={CAMP.detailStart}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50">
              <Play size={14} /> Start
            </button>
          )}
          {writable && st === "RUNNING" && (
            <button onClick={() => runAction("Paused", "POST", `/campaigns/${campaign_id}/pause`)}
              disabled={busy} data-testid={CAMP.detailPause}
              className="inline-flex items-center gap-1 rounded-md bg-amber-500 text-white px-3 py-1.5 text-sm hover:bg-amber-600 disabled:opacity-50">
              <Pause size={14} /> Pause
            </button>
          )}
          {writable && st === "PAUSED" && (
            <button onClick={() => runAction("Resumed", "POST", `/campaigns/${campaign_id}/resume`)}
              disabled={busy} data-testid={CAMP.detailResume}
              className="inline-flex items-center gap-1 rounded-md bg-emerald-600 text-white px-3 py-1.5 text-sm hover:bg-emerald-700 disabled:opacity-50">
              <Play size={14} /> Resume
            </button>
          )}
          {writable && (st === "RUNNING" || st === "PAUSED" || st === "DRAFT") && (
            <button onClick={() => runAction("Stopped", "POST", `/campaigns/${campaign_id}/stop`)}
              disabled={busy} data-testid={CAMP.detailStop}
              className="inline-flex items-center gap-1 rounded-md bg-rose-600 text-white px-3 py-1.5 text-sm hover:bg-rose-700 disabled:opacity-50">
              <Square size={14} /> Stop
            </button>
          )}
        </div>
      </div>

      {notice && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}
      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      {/* Report cards */}
      {report && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <StatCard label="Total leads" value={report.total_leads}
            testId={CAMP.reportTotal} />
          <StatCard label="Queued" value={report.queued} testId={CAMP.reportQueued} />
          <StatCard label="In progress" value={report.in_progress} />
          <StatCard label="Renewed" value={report.renewed}
            tone="text-emerald-700" testId={CAMP.reportRenewed} />
          <StatCard label="Escalated" value={report.escalated}
            tone="text-rose-700" testId={CAMP.reportEscalated} />
          <StatCard label="Revenue"
            value={`₹${Number(report.revenue || 0).toLocaleString("en-IN")}`}
            tone="text-emerald-800" testId={CAMP.reportRevenue} />
        </div>
      )}
      {report && (
        <div className="text-xs text-slate-500 flex items-center gap-2">
          <TrendingUp size={12} />
          Potential revenue if every queued lead converts:{" "}
          <span className="font-mono text-slate-700" data-testid={CAMP.reportPotential}>
            ₹{Number(report.potential_revenue || 0).toLocaleString("en-IN")}
          </span>
        </div>
      )}

      {/* Config & Filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-900">Configuration</h2>
          {writable && st !== "STOPPED" && st !== "COMPLETED" && (
            editingFilters ? (
              <div className="flex gap-2">
                <button onClick={() => { setEditingFilters(false); setFiltersDraft(campaign.filters || {}); }}
                  className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs">Cancel</button>
                <button onClick={saveFilters} disabled={busy} data-testid={CAMP.saveFilters}
                  className="rounded-md bg-slate-900 text-white px-3 py-1 text-xs disabled:opacity-50">Save</button>
              </div>
            ) : (
              <button onClick={() => setEditingFilters(true)} data-testid={CAMP.editFilters}
                className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs">Edit filters</button>
            )
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
          <div>
            <div className="text-slate-500">Expiry window</div>
            <div className="font-mono text-slate-800">{campaign.expiry_days_window} days</div>
          </div>
          <div>
            <div className="text-slate-500">Max attempts</div>
            <div className="font-mono text-slate-800">{campaign.max_attempts}</div>
          </div>
          <div>
            <div className="text-slate-500">Retry interval</div>
            <div className="font-mono text-slate-800">{campaign.retry_interval_hours} h</div>
          </div>
          <div>
            <div className="text-slate-500">Started</div>
            <div className="font-mono text-slate-800">
              {campaign.started_at ? new Date(campaign.started_at).toLocaleString() : "—"}
            </div>
          </div>
        </div>
        {editingFilters ? (
          <div className="mt-4 border-t border-slate-100 pt-3 grid grid-cols-2 gap-3 text-xs">
            <label className="block">
              <span className="text-slate-500">Customer type</span>
              <select value={filtersDraft.customer_type || ""}
                onChange={(e) => setFiltersDraft({ ...filtersDraft, customer_type: e.target.value || undefined })}
                className="mt-1 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm">
                <option value="">Any</option>
                <option value="WALK_IN">Walk-in</option>
                <option value="CORPORATE">Corporate</option>
              </select>
            </label>
            <label className="flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!filtersDraft.exclude_previously_renewed}
                onChange={(e) => setFiltersDraft({ ...filtersDraft, exclude_previously_renewed: e.target.checked })} />
              Exclude previously renewed
            </label>
            <label className="block">
              <span className="text-slate-500">Min laptop value (₹)</span>
              <input type="number" value={filtersDraft.laptop_value_min ?? ""}
                onChange={(e) => setFiltersDraft({ ...filtersDraft, laptop_value_min: e.target.value ? Number(e.target.value) : undefined })}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <label className="block">
              <span className="text-slate-500">Max laptop value (₹)</span>
              <input type="number" value={filtersDraft.laptop_value_max ?? ""}
                onChange={(e) => setFiltersDraft({ ...filtersDraft, laptop_value_max: e.target.value ? Number(e.target.value) : undefined })}
                className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
          </div>
        ) : (
          <div className="mt-4 border-t border-slate-100 pt-3 text-xs text-slate-600">
            <span className="text-slate-500">Filters: </span>
            <span className="font-mono">
              {Object.keys(campaign.filters || {}).length
                ? JSON.stringify(campaign.filters)
                : "none (all eligible customers)"}
            </span>
          </div>
        )}
      </div>

      {/* Actions */}
      {writable && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm flex flex-wrap items-center gap-2">
          <div className="text-sm font-semibold text-slate-900 mr-2 flex items-center gap-1">
            <Users size={14} /> Enrollment
          </div>
          <button onClick={runPreview} disabled={busy} data-testid={CAMP.detailPreview}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">
            <ClipboardList size={14} /> Preview eligible
          </button>
          <button onClick={() => runAction("Enrolled", "POST", `/campaigns/${campaign_id}/enroll`)}
            disabled={busy || (st !== "DRAFT" && st !== "RUNNING" && st !== "PAUSED")}
            data-testid={CAMP.detailEnroll}
            className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white px-3 py-1.5 text-xs hover:bg-slate-800 disabled:opacity-50">
            <Users size={14} /> Enrol leads
          </button>
          <button onClick={() => runAction("Retry sweep done", "POST", `/campaigns/${campaign_id}/process-retries`)}
            disabled={busy} data-testid={CAMP.detailProcessRetries}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs hover:bg-slate-50">
            <RotateCw size={14} /> Process retries
          </button>
          {preview && (
            <span className="ml-2 text-xs text-slate-500">
              → {preview.total} passport(s) match current filters.
            </span>
          )}
        </div>
      )}

      {/* Leads */}
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="text-sm font-semibold text-slate-900">Leads</div>
          <div className="flex items-center gap-2">
            <input value={leadsMobile}
              onChange={(e) => { setLeadsMobile(e.target.value); setLeadsPage(1); }}
              data-testid={CAMP.leadsSearch}
              placeholder="Mobile"
              className="rounded-md border border-slate-200 px-3 py-1.5 text-xs" />
            <select value={leadsStatus}
              onChange={(e) => { setLeadsStatus(e.target.value); setLeadsPage(1); }}
              data-testid={CAMP.leadsStatusFilter}
              className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs">
              <option value="">All statuses</option>
              {Object.keys(LEAD_STATUS_CLS).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <table className="w-full text-sm" data-testid={CAMP.leadsTable}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Customer · Mobile</th>
              <th className="text-left px-4 py-3">Passport</th>
              <th className="text-left px-4 py-3">Expiry</th>
              <th className="text-right px-4 py-3">Price</th>
              <th className="text-right px-4 py-3">Attempts</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Payment link</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-14 text-center text-slate-400">No leads yet.</td></tr>
            )}
            {leads.map((l) => (
              <tr key={l.id} data-testid={CAMP.leadsRow(l.id)}
                className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="text-slate-900">{l.customer_name || "—"}</div>
                  <div className="text-xs text-slate-500 font-mono">{l.mobile || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs font-mono">
                  <div>{l.internal_barcode || "—"}</div>
                  <div className="text-slate-500">{l.manufacturer_serial || ""}</div>
                </td>
                <td className="px-4 py-3 text-xs">{l.expiry_date || "—"}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">
                  <span className="inline-flex items-center gap-0.5">
                    <IndianRupee size={10} />{Number(l.computed_price || 0).toLocaleString("en-IN")}
                  </span>
                </td>
                <td className="px-4 py-3 text-right font-mono text-xs">{l.attempts_count || 0}</td>
                <td className="px-4 py-3">
                  <Chip text={l.status} cls={LEAD_STATUS_CLS[l.status]} />
                </td>
                <td className="px-4 py-3 text-xs">
                  {l.payment_link_url ? (
                    <a href={l.payment_link_url} target="_blank" rel="noreferrer"
                      data-testid={CAMP.leadPaymentLinkAnchor(l.id)}
                      className="inline-flex items-center gap-1 text-indigo-700 hover:underline">
                      Open <ExternalLink size={10} />
                    </a>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {writable && !["PAID", "RENEWED", "EXPIRED_UNCONVERTED", "WRONG_NUMBER", "NOT_INTERESTED", "DND"].includes(l.status) && (
                    <button onClick={() => generatePaymentLink(l.id)}
                      disabled={busy || Number(l.computed_price || 0) <= 0}
                      data-testid={CAMP.leadGeneratePaymentLink(l.id)}
                      className="rounded-md bg-indigo-600 text-white px-3 py-1 text-xs hover:bg-indigo-700 disabled:opacity-40">
                      Generate link
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 text-xs text-slate-500 border-t border-slate-100">
          <span>{leadsMeta.total} lead{leadsMeta.total === 1 ? "" : "s"}</span>
          <div className="flex items-center gap-2">
            <button data-testid={CAMP.leadsPagePrev} disabled={leadsPage <= 1}
              onClick={() => setLeadsPage(leadsPage - 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Prev</button>
            <span>Page {leadsPage} of {leadsMeta.total_pages || 1}</span>
            <button data-testid={CAMP.leadsPageNext} disabled={leadsPage >= (leadsMeta.total_pages || 1)}
              onClick={() => setLeadsPage(leadsPage + 1)}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 disabled:opacity-40">Next</button>
          </div>
        </div>
      </div>
    </div>
  );
}
